import './index.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { keyboardMatch, keyboardKeys, findNextFocusableElement } from '@hh.ru/magritte-common-keyboard';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { CustomChip } from '@hh.ru/magritte-ui-chips';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { ChevronDownOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { useFocus } from '@hh.ru/magritte-ui-input/useFocus';

var styles = {"root":"magritte-root___-zVdz_3-3-17","focused":"magritte-focused___0Uzyu_3-3-17","invalid":"magritte-invalid___tVzQV_3-3-17","disabled":"magritte-disabled___UeqcV_3-3-17","label":"magritte-label___j7Trt_3-3-17","focus-visible":"magritte-focus-visible___ifvAP_3-3-17","focusVisible":"magritte-focus-visible___ifvAP_3-3-17","large":"magritte-large___JIrIy_3-3-17","field":"magritte-field___rlEmn_3-3-17","icon":"magritte-icon___fDMiz_3-3-17","scrollable-area":"magritte-scrollable-area___-xMF2_3-3-17","scrollableArea":"magritte-scrollable-area___-xMF2_3-3-17","medium":"magritte-medium___Xk-XB_3-3-17","chips-and-input":"magritte-chips-and-input___qg-Ba_3-3-17","chipsAndInput":"magritte-chips-and-input___qg-Ba_3-3-17","field-input":"magritte-field-input___04Cag_3-3-17","fieldInput":"magritte-field-input___04Cag_3-3-17","label-wrapper":"magritte-label-wrapper___De7Bm_3-3-17","labelWrapper":"magritte-label-wrapper___De7Bm_3-3-17","icon_expanded":"magritte-icon_expanded___RaOT-_3-3-17","iconExpanded":"magritte-icon_expanded___RaOT-_3-3-17","custom-chip-wrapper":"magritte-custom-chip-wrapper___a9kJ6_3-3-17","customChipWrapper":"magritte-custom-chip-wrapper___a9kJ6_3-3-17"};

const focusOnLastChip = (event, chipsContainer, input) => {
    if (!chipsContainer || !input) {
        return;
    }
    const nextFocusableElement = findNextFocusableElement(chipsContainer, input, 'left', 'list');
    if (nextFocusableElement !== null) {
        event.preventDefault();
        nextFocusableElement.focus();
    }
};
const ChipsTrigger = forwardRef(({ size = 'large', placeholder, disabled, expanded: externalExpanded, toggleExpanded: toggleExternalExpanded, state, invalid, maxHeight, description, errorMessage, wrapperRef, onChange, onFocus, onBlur, onKeyDown, onChangeInput: onChangeInputExternal, allowArbitraryValues = true, 'data-qa': dataQa = 'chips-trigger', }, ref) => {
    const { isMobile } = useBreakpoint();
    const [expanded, setExpanded] = useState(false);
    const [isFocusVisible, setFocusVisible] = useState(false);
    const stateRef = useRef({ hadFocusWhenClicked: false, wasExpandedWhenClicked: false });
    const chipsRootRef = useRef(null);
    const chipsRootRefCallback = useMultipleRefs(wrapperRef, chipsRootRef);
    const chipsContainerRef = useRef(null);
    const inputRef = useRef(null);
    const inputRefCallback = useMultipleRefs(ref, inputRef);
    const showLabel = state.value === '' && state.chips.length === 0 && placeholder !== '';
    const onChangeInput = useCallback((value) => {
        onChange({
            value,
            chips: state.chips,
            event: { type: 'change', modifier: 'keyboard' },
        });
        onChangeInputExternal?.(value);
    }, [state.chips, onChange, onChangeInputExternal]);
    const { changeHandler, focusHandler, blurHandler, isFocused } = useFocus(onChangeInput, onFocus, onBlur);
    useEffect(() => {
        if (inputRef.current) {
            setFocusVisible(isFocused && inputRef.current.classList.contains('focus-visible'));
        }
    }, [isFocused]);
    const deleteHandler = useCallback((index) => {
        const deletedChip = state.chips.splice(index, 1);
        onChange({
            value: state.value,
            chips: [...state.chips],
            event: { type: 'remove', modifier: 'cross', element: deletedChip?.[0] },
        });
    }, [onChange, state.chips, state.value]);
    const keyDownHandler = useCallback((event) => {
        // создаем новый чипс и затираем текстовый инпут
        if (allowArbitraryValues && keyboardMatch(event.nativeEvent, keyboardKeys.Enter)) {
            if (state.value.length === 0) {
                return;
            }
            onChange({
                value: '',
                chips: [...state.chips, { value: state.value, userInput: true }],
                event: { type: 'add', modifier: 'enter' },
            });
            onChangeInputExternal?.('');
        }
        // удаляем последний созданный чипс
        if (state.value === '' && keyboardMatch(event.nativeEvent, keyboardKeys.Backspace)) {
            if (state.chips.length === 0) {
                return;
            }
            const deletedChip = state.chips.pop();
            onChange({
                value: state.value,
                chips: [...state.chips],
                event: { type: 'remove', modifier: 'backspace', element: deletedChip },
            });
        }
        // пытаемся найти ближайший чипс для фокуса
        if (state.value === '' && keyboardMatch(event.nativeEvent, keyboardKeys.ArrowLeft)) {
            focusOnLastChip(event, chipsContainerRef.current, inputRef.current);
        }
    }, [allowArbitraryValues, onChange, onChangeInputExternal, state]);
    // при выставлении высоты учитваем падинги
    // иначе высота будет больше значения свойства
    const scrollableAreaStyle = maxHeight
        ? { maxHeight: `${maxHeight - (size === 'medium' ? 16 : 24)}px` }
        : undefined;
    return (jsxs("div", { children: [jsxs("div", { ref: chipsRootRefCallback, className: classnames(styles.root, styles[size], {
                    [styles.focused]: isFocused,
                    [styles.focusVisible]: isFocusVisible,
                    [styles.disabled]: !!disabled,
                    [styles.invalid]: !!invalid,
                }), onFocus: () => {
                    if (disabled) {
                        return;
                    }
                    if (!inputRef.current) {
                        return;
                    }
                    setExpanded(true);
                    // добавляем .focus() еще и тут
                    // чтобы не сбрасывать фокус во время клика по chip
                    // или по клику на враппер
                    if (document.activeElement === chipsRootRef.current) {
                        inputRef.current.focus();
                    }
                }, onBlur: (event) => {
                    stateRef.current.hadFocusWhenClicked &&=
                        event.relatedTarget === chipsRootRef.current || event.relatedTarget === inputRef.current;
                    setExpanded(false);
                }, onKeyDown: onKeyDown, onMouseDown: () => {
                    // при повторном клике по инпуту в фокусе нужно закрыть дроп
                    // при этом клик может прийтись на обертку, из-за чего инпут поймает blur и повторный фокус
                    // что усложняет проверку того, что инпут уже был в фокусе в момент клика
                    // поэтому в цепочке mouseDown -> blur -> focus -> click храним флаг, что фокус не вышел за пределы компонента
                    stateRef.current.hadFocusWhenClicked = document.activeElement === inputRef.current;
                    stateRef.current.wasExpandedWhenClicked = !!externalExpanded;
                }, onClick: () => {
                    if (stateRef.current.hadFocusWhenClicked &&
                        stateRef.current.wasExpandedWhenClicked === externalExpanded) {
                        toggleExternalExpanded?.();
                    }
                    stateRef.current.hadFocusWhenClicked = false;
                    stateRef.current.wasExpandedWhenClicked = false;
                }, tabIndex: disabled ? -1 : 0, "data-qa": dataQa, children: [jsx("div", { style: scrollableAreaStyle, className: styles.scrollableArea, children: jsxs("div", { ref: chipsContainerRef, className: styles.chipsAndInput, children: [state.chips.map((chip, index) => {
                                    return (jsx("div", { className: styles.customChipWrapper, onMouseDown: (event) => event.preventDefault(), children: jsx(CustomChip, { Element: "button", size: "small", disabled: disabled, onDelete: () => {
                                                deleteHandler(index);
                                            }, dataQaWrapper: `chips-trigger-chip-${chip.value}`, deleteOnly: true, children: chip.label ?? chip.value }) }, `${chip.value}-${index}`));
                                }), jsxs("div", { className: styles.field, children: [jsx("input", { ref: inputRefCallback, className: styles.fieldInput, type: "text", disabled: disabled, value: state.value, onKeyDown: keyDownHandler, onFocus: focusHandler, onBlur: blurHandler, onChange: changeHandler, "data-qa": "chips-trigger-input", readOnly: isMobile }), showLabel && (jsx("div", { className: styles.labelWrapper, children: jsx("label", { className: styles.label, children: placeholder }) }))] })] }) }), jsx("div", { className: classnames(styles.icon, {
                            [styles.iconExpanded]: expanded || externalExpanded,
                        }), children: jsx(ChevronDownOutlinedSize24, {}) })] }), jsx(FormHelper, { description: description, errorMessage: errorMessage, disabled: disabled, invalid: invalid })] }));
});
ChipsTrigger.displayName = 'ChipsTrigger';

export { ChipsTrigger };
//# sourceMappingURL=ChipsTrigger.js.map
